'use client';

import { useMovieShowtimes } from '@/hooks/showtimes/movie-showtimes';
import { BasicShowtime } from '@/types/showtimes';
import {
  filterActiveTheaterShowtimeGroups,
  hasAnyActiveTheaterShowtime,
  selectTrailerShowtimeTheater,
} from '@/utils/showtime-filters';
import { useMemo } from 'react';

type UseTrailerShowtimesParams = {
  date?: Date;
  enabled?: boolean;
  movieId: number | undefined;
  preferredTheaterId?: number;
  zipCode?: string;
};

type UseTrailerShowtimesResult = {
  hasActiveShowtimes: boolean;
  isShowtimesLoading: boolean;
  showtimes: BasicShowtime[];
  theaterId?: number;
  theaterName?: string;
  timeZone?: string;
};

export const useTrailerShowtimes = ({
  date,
  enabled = true,
  movieId,
  preferredTheaterId,
  zipCode,
}: UseTrailerShowtimesParams): UseTrailerShowtimesResult => {
  const {
    data: movieShowtimesResponse,
    isFetching,
    isLoading,
  } = useMovieShowtimes({ date, enabled, movieId, zipCode });

  const selectedTheater = useMemo(
    () => selectTrailerShowtimeTheater(movieShowtimesResponse?.theaters ?? [], preferredTheaterId),
    [movieShowtimesResponse?.theaters, preferredTheaterId]
  );
  const activeShowtimeGroups = useMemo(
    () => selectedTheater
      ? filterActiveTheaterShowtimeGroups(selectedTheater.showtimesByPrintType, { offsetMinutes: 0 })
      : [],
    [selectedTheater]
  );
  const showtimes = useMemo(
    () => activeShowtimeGroups
      .flatMap(group => group.showtimes)
      .sort((first, second) => first.startTime.getTime() - second.startTime.getTime()),
    [activeShowtimeGroups]
  );

  return {
    hasActiveShowtimes: hasAnyActiveTheaterShowtime(activeShowtimeGroups),
    isShowtimesLoading: enabled && !!zipCode && !movieShowtimesResponse && (isLoading || isFetching),
    showtimes,
    theaterId: selectedTheater?.theaterId,
    theaterName: selectedTheater?.theaterName,
    timeZone: selectedTheater?.timeZone,
  };
};
